import {Injectable, effect, inject, signal} from '@angular/core';
import * as Sentry from '@sentry/angular';
import {firstValueFrom} from 'rxjs';
import {User} from '../models/user.model';
import {ConsentService} from './consent.service';
import {OrderFormFactoryService} from './comeback-link/order-form-factory.service';
import {USER_SESSION_STORAGE_KEY, UserWebService} from './user.web.service';

/**
 * Identifies the customer on Sentry events, but only while the customer has consented
 * to analytics. Revoking consent detaches the identity again.
 *
 * Two identity sources are combined:
 * - The id of the authenticated user (dashboard, resolved via the backend once logged in).
 * - The email entered or restored anywhere in the booking funnel.
 */
@Injectable({providedIn: 'root'})
export class SentryUserService {
  private readonly consentService = inject(ConsentService);
  private readonly orderFormFactory = inject(OrderFormFactoryService);
  private readonly userWebService = inject(UserWebService);
  private readonly mailControl = this.orderFormFactory.userForm.controls.mail;

  private readonly authenticatedUserId = signal<string | null>(null);
  private userRequestPending = false;

  private readonly syncSentryUserEffect = effect(() => {
    if (!this.consentService.analyticsConsented()) {
      Sentry.getCurrentScope().setTag('userId', undefined);
      Sentry.setUser(null);
      return;
    }

    const userId = this.authenticatedUserId();
    const mail = this.orderFormFactory.userMail().trim();

    const sentryUser: Sentry.User = {};
    if (userId) {
      sentryUser.id = userId;
    }
    if (mail && this.mailControl.valid) {
      sentryUser.email = mail;
    }

    Sentry.getCurrentScope().setTag('userId', userId ?? undefined);
    Sentry.setUser(Object.keys(sentryUser).length > 0 ? sentryUser : null);
  });

  /**
   * Resolves the id of the authenticated user and attaches it to Sentry events
   * (consent permitting). The user is fetched from the backend once and cached in
   * session storage; subsequent calls are no-ops. Failures are swallowed so route
   * activation never breaks on a missing identity.
   */
  public async identifyAuthenticatedUser(): Promise<void> {
    if (this.authenticatedUserId() || this.userRequestPending) {
      return;
    }

    const cachedUser = this.getUserFromSessionStorage();
    if (cachedUser?.id) {
      this.authenticatedUserId.set(cachedUser.id);
      return;
    }

    this.userRequestPending = true;
    try {
      const user = await firstValueFrom(this.userWebService.getUser());
      sessionStorage.setItem(USER_SESSION_STORAGE_KEY, JSON.stringify(user));
      if (user.id) {
        this.authenticatedUserId.set(user.id);
      }
    } catch {
      // Identifying the user is best effort; errors on this call must not surface.
    } finally {
      this.userRequestPending = false;
    }
  }

  /**
   * Reads the cached authenticated user from session storage.
   *
   * @returns The cached user, or null when absent or unparsable.
   */
  private getUserFromSessionStorage(): User | null {
    const raw = sessionStorage.getItem(USER_SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  }
}
